import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth, formatApiError } from "../lib/auth";
import { Key, Eye, EyeSlash, ShieldCheck } from "@phosphor-icons/react";

/**
 * Two-mode password change screen:
 *  - `forced` (when the user has must_change_password=true after a reset):
 *    no current-password field; the user just sets a new one.
 *  - `normal` (called from Settings or any other place): requires the
 *    current password.
 */
export default function ChangePassword() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const forced = !!user?.must_change_password;

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [next2, setNext2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 6) { setErr("La nuova password deve avere almeno 6 caratteri."); return; }
    if (next !== next2) { setErr("Le due nuove password non corrispondono."); return; }
    if (!forced && !current) { setErr("Inserisci la password attuale."); return; }
    setLoading(true);
    try {
      const body = forced ? { new_password: next } : { current_password: current, new_password: next };
      await api.post("/me/password", body);
      // Refresh user so the forced flag clears, then redirect.
      if (refreshUser) await refreshUser();
      navigate(location.state?.from || "/", { replace: true });
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-5 py-12 fade-in" data-testid="change-password-page">
      <div className="flex items-center gap-2 mb-6">
        <Key weight="duotone" size={26} className="text-[#9C5B42]" />
        <h1 className="font-serif text-3xl text-[#1E2520]">
          {forced ? "Imposta la tua nuova password" : "Cambia password"}
        </h1>
      </div>

      {forced && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 mb-6 flex gap-3" data-testid="forced-change-banner">
          <ShieldCheck weight="duotone" size={26} className="text-amber-700 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 leading-relaxed">
            <strong>Stai usando una password temporanea.</strong>{" "}
            Per continuare devi impostarne una nuova, conosciuta solo da te.
            La password temporanea verrà invalidata subito dopo.
          </div>
        </div>
      )}

      <form onSubmit={submit} className="card p-6 md:p-8 space-y-4">
        {!forced && (
          <div>
            <label className="label-eyebrow block mb-2">Password attuale</label>
            <input
              type={show ? "text" : "password"}
              className="input-base"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
              data-testid="change-pwd-current"
            />
          </div>
        )}
        <div>
          <label className="label-eyebrow block mb-2">Nuova password</label>
          <div className="relative">
            <input
              type={show ? "text" : "password"}
              className="input-base pr-12"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              minLength={6}
              required
              data-testid="change-pwd-new"
            />
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C635A] hover:text-[#1E2520]"
              aria-label={show ? "Nascondi" : "Mostra"}
            >
              {show ? <EyeSlash size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <p className="text-xs text-[#5C635A] mt-1.5">Minimo 6 caratteri.</p>
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Conferma nuova password</label>
          <input
            type={show ? "text" : "password"}
            className="input-base"
            value={next2}
            onChange={(e) => setNext2(e.target.value)}
            autoComplete="new-password"
            minLength={6}
            required
            data-testid="change-pwd-new2"
          />
        </div>

        {err && (
          <div className="border border-red-300 bg-red-50 text-red-800 px-4 py-3 rounded-sm text-sm" data-testid="change-pwd-error">
            {err}
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={loading}
            className="btn-primary inline-flex items-center justify-center gap-2 flex-1"
            data-testid="change-pwd-submit"
          >
            {loading ? "Aggiornamento..." : "Salva nuova password"}
          </button>
          {!forced && (
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="btn-ghost"
              data-testid="change-pwd-cancel"
            >
              Annulla
            </button>
          )}
        </div>
        {forced && (
          <button
            type="button"
            onClick={logout}
            className="text-xs text-[#5C635A] underline mt-2"
            data-testid="change-pwd-logout"
          >
            Esci dall'account
          </button>
        )}
      </form>
    </div>
  );
}
